import { computed, inject, type Signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AppearanceEditService } from '../appearance-editor/appearance-edit.service';
import type { EntityEditFacade } from './entity-edit.facade';
import type { AppearanceValue } from '../appearance-editor/appearance-editor';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withAppearanceEditing<T extends EntityEditFacade<any, any>>(
  facade: T,
  config: { defaultIcon: string; canEdit: Signal<boolean> }
) {
  const translate = inject(TranslateService);
  const appearanceEditService = inject(AppearanceEditService);

  const entityIcon = computed(() => {
    const formValues = facade.formValues() as { appearance?: AppearanceValue };
    if (formValues.appearance?.imageUrl) return ''; // Using empty string, but avatar is used instead
    return formValues.appearance?.icon ?? config.defaultIcon;
  });

  const entityIconColor = computed(() => {
    const formValues = facade.formValues() as { appearance?: AppearanceValue };
    if (formValues.appearance?.imageUrl) return null;
    return formValues.appearance?.color ?? null;
  });

  const entityAvatarSrc = computed(() => {
    const formValues = facade.formValues() as { appearance?: AppearanceValue };
    return formValues.appearance?.imageUrl ?? null;
  });

  const canEditAppearance = config.canEdit;

  const onEditAppearance = () => {
    if (!canEditAppearance()) return;

    appearanceEditService
      .open({
        title: translate.instant(`${facade.config.i18nPrefix}.sections.appearance`),
        defaultIcon: config.defaultIcon,
        value: (facade.form.controls['appearance']?.value as AppearanceValue) ?? { icon: null, color: null, imageUrl: null },
        iconLabel: translate.instant(`${facade.config.i18nPrefix}.fields.icon`),
        colorLabel: translate.instant(`${facade.config.i18nPrefix}.fields.color`),
        imageUrlLabel: translate.instant(`${facade.config.i18nPrefix}.fields.imageUrl`),
      })
      .subscribe((value: AppearanceValue) => {
        const patch: Record<string, unknown> = { appearance: value };
        facade.form.patchValue(patch);
        facade.form.markAsDirty();
      });
  };

  return {
    entityIcon,
    entityIconColor,
    entityAvatarSrc,
    canEditAppearance,
    onEditAppearance,
  };
}
